import { dequal as deepEqual } from 'dequal/lite';
import lodashGet from 'lodash/fp/get';
import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { ConfigOverride } from '#dn-types/host.ts';
import { tagNameSchema } from '#schemas/tag/name.ts';
import { makeOverrideDefaults } from '#screens/hosts/edit/makeOverrideDefaults.ts';
import { mapOverridesToForm } from '#screens/hosts/form/advanced/mappers/mapOverridesToForm.ts';
import { useTagOverrideMap } from '#screens/hosts/form/hooks/useTagOverrideMap.ts';
import type { HostFormData, HostFormTransformedData } from '#screens/hosts/form/host-form-schema.ts';

/**
 * Update the default values of overrides if tags with config have been added/removed,
 * thus changing the overrideDefaults.
 */
export function useResetOverridesFromTags(
  formContext: UseFormReturn<HostFormData, unknown, HostFormTransformedData>,
  hostOverrides: ConfigOverride[]
) {
  const {
    formState: { defaultValues, dirtyFields },
    reset,
    getValues,
    setValue,
  } = formContext;

  const tags = getValues('tags');
  const validTags = tags
    .map((t) => {
      const parseResult = tagNameSchema.safeParse(t);
      if (parseResult.success) {
        return parseResult.data;
      }
      return undefined;
    })
    .filter(Boolean);
  const overrideMap = useTagOverrideMap(validTags);
  const overrideDefaults = makeOverrideDefaults(overrideMap);

  useEffect(() => {
    if (!deepEqual(defaultValues?.configOverrides, overrideDefaults)) {
      reset({ ...defaultValues, configOverrides: overrideDefaults }, { keepDirty: true, keepDirtyValues: true });
      // If we don't set the host override value back in, and the override was same as the tag's value,
      // it won't be dirty and would be cleared out by the reset above.
      if (hostOverrides.length) {
        const formFormattedHostOverrides = mapOverridesToForm(hostOverrides);
        hostOverrides.forEach((o) => {
          // Only reset if the field isn't dirty.  If dirty, we want to keep what the user entered
          if (!lodashGet(`configOverrides.${o.key}`, dirtyFields)) {
            setValue(`configOverrides.${o.key}`, lodashGet(o.key, formFormattedHostOverrides));
          }
        });
      }
    }
  }, [defaultValues, hostOverrides, overrideDefaults, reset, setValue, dirtyFields]);
}
